import React, { FC } from 'react';
import useURLSearchParams from '../../../hooks/useURLSearchParams';
import { RequireAtLeastOne } from '../../../types';
import { getBranchCurrencySymbol } from '../../../utils';
import Colors from '../../../utils/Colors';
import FormContainer from '../../Shared/FormContainer/FormContainer';
import { ErrorMessage } from '../../Shared/Shared';
import ToolBar from '../../ToolBar/ToolBar';
import { isProductNoCharge } from '../helpers';
import { FormItemContainer, FormItemDescription, FormLabel, FormRadioContainer, FormRadioWrapper, From, MainTitle, Price, PriceTitleContainer } from '../styled';
import { FORM_RADIO_COMPONENT_TYPES } from '../types';

interface FormRadioProductsAllProps {
  type: keyof typeof FORM_RADIO_COMPONENT_TYPES;
  formOptions: Record<string, any>;
  dataArr?: (Queries.CollarAPI_Product & { title?: string })[];
  onSubmit?: (data: Record<string, string>) => void;
  checkIfAvailable?: (productId: string) => boolean;
  borderColor?: string;
  loading?: boolean;
  defaultCheckedId?: string;
  isLoggedIn?: boolean;
  AboveItemsElement?: () => JSX.Element;
  noNextButton?: boolean;
  disabledProducts: Queries.CollarAPI_Product[];
  categoriesOptions?: { name: string; id: string }[];
}

type FormRadioProductsProps = RequireAtLeastOne<FormRadioProductsAllProps, 'onSubmit' | 'noNextButton'>;

const FormRadioProducts: FC<FormRadioProductsProps> = ({
  type,
  formOptions,
  dataArr,
  onSubmit,
  checkIfAvailable = () => true,
  borderColor = 'gray',
  loading,
  defaultCheckedId,
  isLoggedIn,
  AboveItemsElement,
  noNextButton,
  disabledProducts,
  categoriesOptions
}) => {
  const { title, registerAs, requiredMessage } = FORM_RADIO_COMPONENT_TYPES[type];
  const { register, handleSubmit, formState } = formOptions;
  const [selectedCategories] = useURLSearchParams<string>('categories');

  const filteredDataArr = selectedCategories.length === 0 ? dataArr : dataArr?.filter(data => data?.ProductCategories?.some(category => selectedCategories?.includes(category.name?.toLowerCase())));

  return (
    <FormRadioWrapper isLoggedIn={isLoggedIn}>
      <FormContainer onSubmit={handleSubmit(onSubmit)} title={title} loading={loading} noPreviousButton error={formState?.errors?.[registerAs]} noNextButton={noNextButton}>
        <ToolBar toolBarButtons={categoriesOptions} urlSearchParam="categories" selectOnlyOne padding="0" />

        {!filteredDataArr?.length && <div>No products found</div>}
        <FormRadioContainer>
          {AboveItemsElement && <AboveItemsElement />}
          {filteredDataArr?.map(data => {
            if (!data) return null;
            const { id, name, price, Flags, short_description, description, title } = data || {};

            const isFree = isProductNoCharge(data);
            const requiresMembership = disabledProducts.some(product => product.id === id);
            const isDisabled = !checkIfAvailable(id) || requiresMembership;

            return (
              <FormLabel key={id} borderColor={borderColor}>
                <input
                  {...register(registerAs, {
                    required: requiredMessage,
                    validate: checkIfAvailable
                  })}
                  type="radio"
                  value={id}
                  autoFocus
                  defaultChecked={id === defaultCheckedId}
                  disabled={isDisabled}
                />
                <FormItemContainer disabled={isDisabled}>
                  <PriceTitleContainer>
                    <MainTitle>{name || title}</MainTitle>
                    <Price>
                      <From>{Flags?.find(flag => flag!.name === 'from_label') ? 'from ' : ''}</From>
                      {isFree ? '' : `${getBranchCurrencySymbol()}${price}`}
                    </Price>
                  </PriceTitleContainer>
                  {requiresMembership && (
                    <>
                      <div>
                        <b style={{ color: Colors.orange }}>Requires Membership</b>
                      </div>
                      <br />
                    </>
                  )}
                  <FormItemDescription
                    dangerouslySetInnerHTML={{
                      __html: short_description || description
                    }}
                  />
                </FormItemContainer>
              </FormLabel>
            );
          })}
        </FormRadioContainer>
        {formState?.errors?.[registerAs] && <ErrorMessage>{formState.errors[registerAs].message}</ErrorMessage>}
      </FormContainer>
    </FormRadioWrapper>
  );
};
export default FormRadioProducts;
